// src/features/tasks/hooks/useTaskCategories.js
import { useState, useCallback } from 'react';
import { tasksAPI } from '../services/tasks';
import { useApi } from '../../../hooks/api/useApi';

export function useTaskCategories() {
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const { loading, error, execute, clearError } = useApi();

  const createTaskCategory = useCallback(async (data) => {
    const result = await execute(() => tasksAPI.createTaskCategory(data));
    if (result.success) {
      await fetchTaskCategories();
    }
    return result;
  }, [execute]);

  const fetchTaskCategories = useCallback(async (params = {}) => {
    const result = await execute(() => tasksAPI.getTaskCategories(params));
    if (result.success) {
      setCategories(result.data.data?.categories || []);
    }
    return result;
  }, [execute]);

  const fetchTaskCategoryDetails = useCallback(async (categoryId) => {
    const result = await execute(() => tasksAPI.getTaskCategoryDetails(categoryId));
    if (result.success) {
      setCurrentCategory(result.data.data);
    }
    return result;
  }, [execute]);

  const updateTaskCategory = useCallback(async (categoryId, data) => {
    const result = await execute(() => tasksAPI.updateTaskCategory(categoryId, data));
    if (result.success) {
      // Update current category if it's the one being updated
      if (currentCategory?.category_id === categoryId) {
        setCurrentCategory(prev => ({ ...prev, ...result.data.data }));
      }
      // Update in categories list
      setCategories(prev => prev.map(cat => 
        cat.category_id === categoryId ? { ...cat, ...result.data.data } : cat
      ));
    }
    return result;
  }, [execute, currentCategory]);

  const deleteTaskCategory = useCallback(async (categoryId, force = false) => {
    const result = await execute(() => tasksAPI.deleteTaskCategory(categoryId, { force }));
    if (result.success) {
      setCategories(prev => prev.filter(cat => cat.category_id !== categoryId));
      if (currentCategory?.category_id === categoryId) {
        setCurrentCategory(null);
      }
    }
    return result;
  }, [execute, currentCategory]);

  const activateTaskCategory = useCallback(async (categoryId) => {
    const result = await execute(() => tasksAPI.activateTaskCategory(categoryId));
    if (result.success) {
      // Update the category status in both current and list
      const updateStatus = (cat) => 
        cat.category_id === categoryId ? { ...cat, is_active: true } : cat;
      
      setCategories(prev => prev.map(updateStatus));
      if (currentCategory?.category_id === categoryId) {
        setCurrentCategory(prev => ({ ...prev, is_active: true }));
      }
    }
    return result;
  }, [execute, currentCategory]);

  const deactivateTaskCategory = useCallback(async (categoryId) => {
    const result = await execute(() => tasksAPI.deactivateTaskCategory(categoryId));
    if (result.success) {
      // Update the category status in both current and list
      const updateStatus = (cat) => 
        cat.category_id === categoryId ? { ...cat, is_active: false } : cat;
      
      setCategories(prev => prev.map(updateStatus));
      if (currentCategory?.category_id === categoryId) {
        setCurrentCategory(prev => ({ ...prev, is_active: false }));
      }
    }
    return result;
  }, [execute, currentCategory]);

  const getCategoryTasks = useCallback(async (categoryId, params = {}) => {
    return await execute(() => tasksAPI.getCategoryTasks(categoryId, params));
  }, [execute]);

  return {
    categories,
    currentCategory,
    loading,
    error,
    clearError,
    createTaskCategory,
    fetchTaskCategories,
    fetchTaskCategoryDetails,
    updateTaskCategory,
    deleteTaskCategory,
    activateTaskCategory,
    deactivateTaskCategory,
    getCategoryTasks,
  };
}